import { Box, Typography, Chip, Stack, alpha, useTheme } from '@mui/material';
import type { Question } from '../types';

interface QuizQuestionCardProps {
  question: Question;
  selectedOptions: string[];
  onOptionClick: (option: string) => void;
  showExplanation: boolean;
  disabled: boolean;
  revealAnswer: boolean;
}

const TYPE_LABEL: Record<string, string> = { single: '单选', multiple: '多选', judge: '判断' };

export default function QuizQuestionCard({
  question, selectedOptions, onOptionClick, showExplanation, disabled, revealAnswer,
}: QuizQuestionCardProps) {
  const theme = useTheme();
  const hasAnswered = selectedOptions.length > 0;
  const isIncompleteMulti = question.type === 'multiple' && selectedOptions.length > 0 && selectedOptions.length < question.answer.length;
  const isCorrect = hasAnswered && !isIncompleteMulti
    ? question.answer.sort().join() === [...selectedOptions].sort().join()
    : null;

  return (
    <Box>
      {/* Question header */}
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', px: 1, pt: 1, pb: 0.5 }}>
        <Chip label={TYPE_LABEL[question.type]} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
      </Stack>

      {/* Question content */}
      <Box sx={{ px: 1, pb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {question.content}
        </Typography>
      </Box>

      {/* Options */}
      <Stack spacing={1} sx={{ py: 1 }}>
        {question.options.map(option => {
          const isSelected = selectedOptions.includes(option.label);
          const isMulti = question.type === 'multiple';
          const isCorrectOption = question.answer.includes(option.label);
          const highlightCorrect = revealAnswer && hasAnswered && isCorrect === false && isCorrectOption;

          return (
            <Box
              key={option.label}
              onClick={() => { if (!disabled) onOptionClick(option.label); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1.5, borderRadius: 3, cursor: disabled ? 'default' : 'pointer',
                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)', userSelect: 'none',
                border: '1.5px solid', minHeight: 48,
                borderColor: highlightCorrect
                  ? theme.palette.success.main
                  : isSelected ? 'primary.main' : 'divider',
                bgcolor: highlightCorrect
                  ? alpha(theme.palette.success.main, 0.1)
                  : isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                '&:active': disabled ? {} : { transform: 'scale(0.99)' },
              }}
            >
              <Box
                sx={{
                  width: 36, height: 36,
                  borderRadius: isMulti ? 2 : '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  border: '1.5px solid', transition: 'all 0.15s ease',
                  borderColor: highlightCorrect
                    ? theme.palette.success.main
                    : isSelected ? 'primary.main' : 'divider',
                  bgcolor: highlightCorrect
                    ? theme.palette.success.main
                    : isSelected ? 'primary.main' : 'transparent',
                  color: (highlightCorrect || isSelected)
                    ? (theme.palette.primary.contrastText || '#fff')
                    : 'text.secondary',
                  fontWeight: 600, fontSize: '0.875rem',
                }}
              >
                {option.label}
              </Box>
              <Typography
                variant="body1"
                sx={{
                  flex: 1, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: highlightCorrect ? 'success.main'
                    : isSelected ? 'text.primary' : 'text.secondary',
                  fontWeight: (highlightCorrect || isSelected) ? 500 : 400,
                }}
              >
                {option.content}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Explanation */}
      {showExplanation && question.explanation && (
        <Box sx={{ mt: 2, p: 2.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>解析</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
            {question.explanation}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
